import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useActiveMunicipio } from '@/context/ActiveMunicipioContext';
import { MUNICIPIO_SIADAP_ID, MUNICIPIO_FORMACAO_ID } from '@/lib/municipios';

interface UnidadeRow {
  id: string;
  nome: string;
  tipo: string;
  unidadePaiId: string | null;
  ordem: number;
}

interface UnidadeTree extends UnidadeRow {
  filhos: UnidadeTree[];
}

type ModalMode = 'nova-raiz' | 'novo-filho' | 'editar';

interface ModalState {
  mode: ModalMode;
  paiId?: string;
  paiNome?: string;
  paiNivel?: number;
  item?: UnidadeRow;
}

const NIVEL_LABELS = ['Unidade', 'Subunidade', 'Sub-subunidade'];
const NIVEL_ICONS = ['ri-building-2-line', 'ri-git-branch-line', 'ri-git-commit-line'];
const NIVEL_COLORS = [
  'bg-slate-100 text-slate-600',
  'bg-white border border-slate-200 text-slate-400',
  'bg-slate-50 border border-slate-100 text-slate-300',
];

function buildTree(rows: UnidadeRow[]): UnidadeTree[] {
  const map: Record<string, UnidadeTree> = {};
  rows.forEach((r) => { map[r.id] = { ...r, filhos: [] }; });
  const roots: UnidadeTree[] = [];
  rows.forEach((r) => {
    if (r.unidadePaiId && map[r.unidadePaiId]) {
      map[r.unidadePaiId].filhos.push(map[r.id]);
    } else if (!r.unidadePaiId) {
      roots.push(map[r.id]);
    }
  });
  function sortNode(n: UnidadeTree) {
    n.filhos.sort((a, b) => a.ordem - b.ordem);
    n.filhos.forEach(sortNode);
  }
  roots.sort((a, b) => a.ordem - b.ordem);
  roots.forEach(sortNode);
  return roots;
}

function countAll(node: UnidadeTree): number {
  return node.filhos.reduce((s, f) => s + 1 + countAll(f), 0);
}

function collectDescendants(node: UnidadeTree): UnidadeTree[] {
  const result: UnidadeTree[] = [];
  function walk(n: UnidadeTree) {
    result.push(n);
    n.filhos.forEach(walk);
  }
  walk(node);
  return result;
}

export default function UnidadesTab() {
  const { activeMunicipio } = useActiveMunicipio();
  const [allRows, setAllRows] = useState<UnidadeRow[]>([]);
  const [tree, setTree] = useState<UnidadeTree[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [modal, setModal] = useState<ModalState | null>(null);
  const [inputNome, setInputNome] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<UnidadeRow | null>(null);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [reorderingId, setReorderingId] = useState<string | null>(null);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  const municipioId = activeMunicipio?.id ?? MUNICIPIO_SIADAP_ID;
  const isSiadap = municipioId === MUNICIPIO_SIADAP_ID;

  // Sincroniza todas as unidades do SIADAP para a Formação
  const syncToFormacao = useCallback(async () => {
    if (!isSiadap) return;
    try {
      // 1. Buscar todas as unidades do SIADAP
      const { data: siadapUnits, error: fetchErr } = await supabase
        .from('unidades_organicas')
        .select('id, nome, tipo, unidade_pai_id, ordem')
        .eq('municipio_id', MUNICIPIO_SIADAP_ID)
        .order('ordem');
      if (fetchErr || !siadapUnits) return;

      // 2. Buscar unidades já existentes na Formação (para detetar as que foram sincronizadas)
      const { data: formacaoUnits } = await supabase
        .from('unidades_organicas')
        .select('id, nome, tipo, unidade_pai_id, ordem')
        .eq('municipio_id', MUNICIPIO_FORMACAO_ID);

      // Mapa nome+tipo -> id na Formação (para detetar duplicados por nome)
      const formacaoByNome = new Map<string, string>();
      (formacaoUnits ?? []).forEach((u) => {
        formacaoByNome.set(`${u.tipo}::${u.nome}`, u.id as string);
      });

      // 3. Construir mapa de IDs SIADAP -> IDs Formação
      // Primeiro as unidades raiz (sem pai), depois as subunidades
      const idMap: Record<string, string> = {};

      // Processar em ordem: primeiro raiz, depois filhos
      const sorted = [...siadapUnits].sort((a, b) => {
        const aHasPai = a.unidade_pai_id ? 1 : 0;
        const bHasPai = b.unidade_pai_id ? 1 : 0;
        return aHasPai - bHasPai;
      });

      const toUpsert: Array<Record<string, unknown>> = [];

      for (const unit of sorted) {
        const key = `${unit.tipo}::${unit.nome}`;
        let formacaoId = formacaoByNome.get(key);

        if (!formacaoId) {
          // Criar novo ID para esta unidade na Formação
          formacaoId = crypto.randomUUID();
          formacaoByNome.set(key, formacaoId);
        }

        idMap[unit.id as string] = formacaoId;

        const paiIdFormacao = unit.unidade_pai_id
          ? (idMap[unit.unidade_pai_id as string] ?? null)
          : null;

        toUpsert.push({
          id: formacaoId,
          nome: unit.nome,
          tipo: unit.tipo,
          unidade_pai_id: paiIdFormacao,
          ordem: unit.ordem,
          municipio_id: MUNICIPIO_FORMACAO_ID,
        });
      }

      if (toUpsert.length > 0) {
        await supabase
          .from('unidades_organicas')
          .upsert(toUpsert, { onConflict: 'id' });
      }
    } catch {
      // Sincronização silenciosa — não bloqueia o utilizador
    }
  }, [isSiadap]);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('unidades_organicas')
      .select('*')
      .eq('municipio_id', municipioId)
      .order('ordem');
    if (data) {
      const rows: UnidadeRow[] = data.map((r) => ({
        id: r.id as string,
        nome: r.nome as string,
        tipo: r.tipo as string,
        unidadePaiId: r.unidade_pai_id as string | null,
        ordem: r.ordem as number,
      }));
      setAllRows(rows);
      setTree(buildTree(rows));
    }
    setLoading(false);
  }, [municipioId]);

  useEffect(() => { load(); }, [load]);

  function showMsg(type: 'success' | 'error', text: string) {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 4000);
  }

  function openModal(state: ModalState) {
    setInputNome(state.item?.nome ?? '');
    setModal(state);
  }

  function closeModal() { setModal(null); setInputNome(''); }

  function getNivel(id: string | null): number {
    if (!id) return 0;
    const row = allRows.find((r) => r.id === id);
    if (!row) return 0;
    return 1 + getNivel(row.unidadePaiId);
  }

  // Reordenar: troca a ordem entre dois irmãos
  async function handleReorder(nodeId: string, direction: 'up' | 'down') {
    const node = allRows.find((r) => r.id === nodeId);
    if (!node) return;

    const siblings = allRows
      .filter((r) => r.unidadePaiId === node.unidadePaiId)
      .sort((a, b) => a.ordem - b.ordem);

    const idx = siblings.findIndex((r) => r.id === nodeId);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= siblings.length) return;

    const swapNode = siblings[swapIdx];
    setReorderingId(nodeId);

    try {
      const [errA, errB] = await Promise.all([
        supabase.from('unidades_organicas').update({ ordem: swapNode.ordem }).eq('id', node.id).then(r => r.error),
        supabase.from('unidades_organicas').update({ ordem: node.ordem }).eq('id', swapNode.id).then(r => r.error),
      ]);
      if (errA || errB) throw new Error('Erro ao reordenar');
      await load();
      syncToFormacao();
    } catch (err: unknown) {
      showMsg('error', err instanceof Error ? err.message : 'Erro ao reordenar.');
    } finally {
      setReorderingId(null);
    }
  }

  // Drag & drop reordenar
  function handleDragStart(e: React.DragEvent, nodeId: string) {
    setDraggedId(nodeId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', nodeId);
  }

  function handleDragOver(e: React.DragEvent, nodeId: string) {
    e.preventDefault();
    if (draggedId === nodeId) return;
    const dragged = allRows.find((r) => r.id === draggedId);
    const target = allRows.find((r) => r.id === nodeId);
    if (!dragged || !target) return;
    // Só permitir drop entre irmãos (mesmo pai)
    if (dragged.unidadePaiId !== target.unidadePaiId) return;
    setDragOverId(nodeId);
  }

  function handleDragLeave() {
    setDragOverId(null);
  }

  async function handleDrop(e: React.DragEvent, targetId: string) {
    e.preventDefault();
    setDragOverId(null);
    const sourceId = e.dataTransfer.getData('text/plain') || draggedId;
    setDraggedId(null);
    if (!sourceId || sourceId === targetId) return;

    const source = allRows.find((r) => r.id === sourceId);
    const target = allRows.find((r) => r.id === targetId);
    if (!source || !target) return;
    if (source.unidadePaiId !== target.unidadePaiId) return;

    const siblings = allRows
      .filter((r) => r.unidadePaiId === source.unidadePaiId)
      .sort((a, b) => a.ordem - b.ordem);

    const sourceIdx = siblings.findIndex((r) => r.id === sourceId);
    const targetIdx = siblings.findIndex((r) => r.id === targetId);
    if (sourceIdx === -1 || targetIdx === -1) return;

    setReorderingId(sourceId);
    try {
      // Reordenar todos os irmãos: remover source, inserir na posição target
      const reordered = [...siblings];
      const [moved] = reordered.splice(sourceIdx, 1);
      reordered.splice(targetIdx, 0, moved);

      // Atualizar ordem de todos
      const updates = reordered.map((r, i) =>
        supabase.from('unidades_organicas').update({ ordem: i + 1 }).eq('id', r.id).then(res => res.error)
      );
      const errors = await Promise.all(updates);
      if (errors.some((e) => e !== null)) throw new Error('Erro ao reordenar');
      await load();
      syncToFormacao();
      showMsg('success', 'Ordem atualizada!');
    } catch (err: unknown) {
      showMsg('error', err instanceof Error ? err.message : 'Erro ao reordenar.');
    } finally {
      setReorderingId(null);
    }
  }

  // Duplicar unidade + filhos recursivamente
  async function handleDuplicate(node: UnidadeTree) {
    setDuplicatingId(node.id);
    try {
      const descendants = collectDescendants(node);
      // Mapear IDs antigos -> novos
      const idMap: Record<string, string> = {};
      descendants.forEach(() => {
        idMap[crypto.randomUUID()] = ''; // placeholder, vamos gerar na inserção
      });
      // Gerar novos IDs
      const newIds = descendants.map(() => crypto.randomUUID());
      descendants.forEach((d, i) => { idMap[d.id] = newIds[i]; });

      // Calcular nova ordem para a raiz
      const rootSiblings = allRows.filter((r) => !r.unidadePaiId);
      const newRootOrdem = rootSiblings.length + 1;

      // Preparar inserts
      const inserts = descendants.map((d, i) => ({
        id: newIds[i],
        nome: i === 0 ? `Cópia de ${d.nome}` : d.nome,
        tipo: d.tipo,
        unidade_pai_id: d.unidadePaiId ? idMap[d.unidadePaiId] : null,
        ordem: d.unidadePaiId
          ? descendants.filter((desc) => desc.unidadePaiId === d.unidadePaiId).findIndex((desc) => desc.id === d.id) + 1
          : newRootOrdem,
        municipio_id: municipioId,
      }));

      // Inserir em batch
      const { error } = await supabase.from('unidades_organicas').insert(inserts);
      if (error) throw error;

      await load();
      syncToFormacao();
      showMsg('success', `"${node.nome}" duplicado com ${descendants.length - 1} subnível${descendants.length - 1 !== 1 ? 'is' : ''}!`);
    } catch (err: unknown) {
      showMsg('error', err instanceof Error ? err.message : 'Erro ao duplicar.');
    } finally {
      setDuplicatingId(null);
    }
  }

  async function handleSave() {
    if (!inputNome.trim() || !modal) return;
    setSaving(true);
    try {
      if (modal.mode === 'editar' && modal.item) {
        const { error } = await supabase
          .from('unidades_organicas')
          .update({ nome: inputNome.trim(), updated_at: new Date().toISOString() })
          .eq('id', modal.item.id);
        if (error) throw error;
        showMsg('success', 'Nome atualizado!');
      } else if (modal.mode === 'nova-raiz') {
        const maxOrdem = allRows.filter((r) => !r.unidadePaiId).length + 1;
        const { error } = await supabase.from('unidades_organicas').insert({
          nome: inputNome.trim(), tipo: 'unidade', unidade_pai_id: null, ordem: maxOrdem, municipio_id: municipioId,
        });
        if (error) throw new Error(`${error.message} (code: ${error.code})`);
        showMsg('success', 'Unidade criada!');
      } else if (modal.mode === 'novo-filho' && modal.paiId) {
        const irmaos = allRows.filter((r) => r.unidadePaiId === modal.paiId);
        const maxOrdem = irmaos.length + 1;
        const nivelFilhoCalc = (modal.paiNivel ?? 0) + 1;
        const tipo = 'subunidade';
        const payload = { nome: inputNome.trim(), tipo, unidade_pai_id: modal.paiId, ordem: maxOrdem, municipio_id: municipioId };
        const { error } = await supabase.from('unidades_organicas').insert(payload);
        if (error) throw new Error(`${error.message} (code: ${error.code}, details: ${error.details})`);
        showMsg('success', `${NIVEL_LABELS[Math.min(nivelFilhoCalc, 2)]} criada!`);
      }
      await load();
      // Sincronizar automaticamente para a Formação (silencioso)
      syncToFormacao();
      closeModal();
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : String(err);
      showMsg('error', `Erro ao guardar: ${errMsg}`);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(item: UnidadeRow) {
    setDeletingId(item.id);
    try {
      const { error } = await supabase.from('unidades_organicas').delete().eq('id', item.id);
      if (error) throw error;
      showMsg('success', `"${item.nome}" eliminado.`);
      await load();
      // Sincronizar automaticamente para a Formação (silencioso)
      syncToFormacao();
    } catch (err: unknown) {
      showMsg('error', err instanceof Error ? err.message : 'Erro ao eliminar.');
    } finally {
      setDeletingId(null);
      setConfirmDelete(null);
    }
  }

  function toggleExpand(id: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function nodeMatchesSearch(node: UnidadeTree, q: string): boolean {
    if (node.nome.toLowerCase().includes(q)) return true;
    return node.filhos.some((f) => nodeMatchesSearch(f, q));
  }

  function filterTree(nodes: UnidadeTree[], q: string): UnidadeTree[] {
    if (!q) return nodes;
    return nodes
      .filter((n) => nodeMatchesSearch(n, q))
      .map((n) => ({ ...n, filhos: filterTree(n.filhos, q) }));
  }

  const searchQuery = search.toLowerCase().trim();
  const visibleTree = filterTree(tree, searchQuery);

  function getExpandedForSearch(nodes: UnidadeTree[]): Set<string> {
    const ids = new Set<string>();
    function collect(ns: UnidadeTree[]) {
      ns.forEach((n) => { ids.add(n.id); collect(n.filhos); });
    }
    collect(nodes);
    return ids;
  }

  const effectiveExpandedIds = searchQuery
    ? getExpandedForSearch(visibleTree)
    : expandedIds;

  function renderNode(node: UnidadeTree, nivel: number, siblings: UnidadeTree[], siblingIdx: number): React.ReactNode {
    const isExpanded = effectiveExpandedIds.has(node.id);
    const hasFilhos = node.filhos.length > 0;
    const nivelIdx = Math.min(nivel, 2);
    const iconClass = NIVEL_ICONS[nivelIdx];
    const colorClass = NIVEL_COLORS[nivelIdx];
    const labelNivel = NIVEL_LABELS[nivelIdx];
    const canAddChild = nivel < 2;
    const indent = nivel * 40;
    const nivelFilho = nivel + 1;
    const isFirst = siblingIdx === 0;
    const isLast = siblingIdx === siblings.length - 1;
    const isReordering = reorderingId === node.id;
    const isSearching = !!searchQuery;
    const isDuplicating = duplicatingId === node.id;
    const isDragOver = dragOverId === node.id && draggedId !== node.id;

    return (
      <div key={node.id}>
        <div
          draggable={!isSearching && !isReordering}
          onDragStart={(e) => handleDragStart(e, node.id)}
          onDragOver={(e) => handleDragOver(e, node.id)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, node.id)}
          className={`flex items-center gap-2 py-2.5 pr-4 hover:bg-slate-50/60 transition-colors group ${
            isDragOver ? 'bg-amber-50/80 border-y border-amber-200' : ''
          } ${!isSearching && !isReordering ? 'cursor-move' : ''}`}
          style={{ paddingLeft: `${24 + indent}px` }}
        >
          {/* Toggle expand */}
          <button
            onClick={() => toggleExpand(node.id)}
            className={`w-5 h-5 flex items-center justify-center text-slate-300 hover:text-slate-600 transition-colors cursor-pointer flex-shrink-0 ${!hasFilhos ? 'invisible' : ''}`}
          >
            <i className={`text-sm ${isExpanded ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'}`} />
          </button>

          {/* Ícone nível */}
          <div className={`w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 ${colorClass}`}>
            <i className={`${iconClass} text-xs`} />
          </div>

          {/* Nome + badge */}
          <div className="flex-1 min-w-0 flex items-center gap-2">
            <button
              onClick={() => openModal({ mode: 'editar', item: node })}
              className={`text-sm truncate text-left cursor-pointer hover:underline underline-offset-2 decoration-slate-400/50 transition-all ${nivel === 0 ? 'font-semibold text-slate-800' : nivel === 1 ? 'font-medium text-slate-700' : 'text-slate-600'}`}
            >
              {node.nome}
            </button>
            <span className="text-xs text-slate-300 flex-shrink-0">{labelNivel}</span>
            {hasFilhos && (
              <span className="text-xs text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-full flex-shrink-0">
                {node.filhos.length}
              </span>
            )}
            {isDuplicating && (
              <span className="text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full flex-shrink-0 flex items-center gap-1">
                <i className="ri-loader-4-line animate-spin text-xs" />
                A duplicar...
              </span>
            )}
          </div>

          {/* Botões de reordenação — sempre visíveis no hover, ocultos durante pesquisa */}
          {!isSearching && (
            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
              <button
                onClick={() => handleReorder(node.id, 'up')}
                disabled={isFirst || isReordering}
                title="Mover para cima"
                className="w-6 h-6 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors cursor-pointer disabled:opacity-20 disabled:cursor-not-allowed"
              >
                {isReordering
                  ? <i className="ri-loader-4-line text-xs animate-spin" />
                  : <i className="ri-arrow-up-s-line text-sm" />
                }
              </button>
              <button
                onClick={() => handleReorder(node.id, 'down')}
                disabled={isLast || isReordering}
                title="Mover para baixo"
                className="w-6 h-6 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors cursor-pointer disabled:opacity-20 disabled:cursor-not-allowed"
              >
                {isReordering
                  ? <i className="ri-loader-4-line text-xs animate-spin" />
                  : <i className="ri-arrow-down-s-line text-sm" />
                }
              </button>
              {/* Separador visual */}
              <div className="w-px h-4 bg-slate-200 mx-1" />
            </div>
          )}

          {/* Ações (duplicar, editar, adicionar filho, eliminar) */}
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
            {/* Duplicar */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDuplicate(node);
              }}
              disabled={isDuplicating}
              title="Duplicar unidade e subníveis"
              className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer disabled:opacity-50"
            >
              {isDuplicating
                ? <i className="ri-loader-4-line text-sm animate-spin" />
                : <i className="ri-file-copy-line text-sm" />
              }
            </button>
            {canAddChild && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openModal({
                    mode: 'novo-filho',
                    paiId: node.id,
                    paiNome: node.nome,
                    paiNivel: nivel,
                  });
                  setExpandedIds((prev) => { const next = new Set(prev); next.add(node.id); return next; });
                }}
                className="whitespace-nowrap flex items-center gap-1 px-2 py-1 text-xs text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
              >
                <i className="ri-add-line text-xs" />
                {NIVEL_LABELS[Math.min(nivelFilho, 2)]}
              </button>
            )}
            <button
              onClick={() => openModal({ mode: 'editar', item: node })}
              className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-edit-line text-sm" />
            </button>
            <button
              onClick={() => setConfirmDelete(node)}
              disabled={deletingId === node.id}
              className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer disabled:opacity-50"
            >
              {deletingId === node.id
                ? <i className="ri-loader-4-line text-sm animate-spin" />
                : <i className="ri-delete-bin-line text-sm" />
              }
            </button>
          </div>
        </div>

        {/* Filhos */}
        {isExpanded && hasFilhos && (
          <div className="border-l border-slate-100 ml-10">
            {node.filhos.map((filho, fi) => renderNode(filho, nivel + 1, node.filhos, fi))}
          </div>
        )}

        {/* Sem filhos expandido */}
        {isExpanded && !hasFilhos && canAddChild && (
          <div
            className="flex items-center gap-2 py-2 text-slate-400"
            style={{ paddingLeft: `${24 + indent + 40}px` }}
          >
            <i className="ri-corner-down-right-line text-xs" />
            <span className="text-xs italic">Sem {NIVEL_LABELS[Math.min(nivelFilho, 2)].toLowerCase()}s</span>
            <button
              onClick={() => {
                openModal({ mode: 'novo-filho', paiId: node.id, paiNome: node.nome, paiNivel: nivel });
              }}
              className="whitespace-nowrap text-xs text-slate-500 hover:text-slate-800 underline cursor-pointer"
            >
              Adicionar
            </button>
          </div>
        )}
      </div>
    );
  }

  const totalRaiz = tree.length;
  const totalTodos = allRows.length;

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-slate-100 rounded-lg">
              <i className="ri-building-2-line text-slate-600 text-base" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-slate-800">Unidades Orgânicas</h2>
              <p className="text-xs text-slate-400 mt-0.5">
                {totalRaiz} unidade{totalRaiz !== 1 ? 's' : ''} · {totalTodos - totalRaiz} sub-nível{totalTodos - totalRaiz !== 1 ? 'is' : ''} · 3 níveis de hierarquia
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isSiadap && (
              <button
                onClick={async () => {
                  setSyncing(true);
                  await syncToFormacao();
                  setSyncing(false);
                  showMsg('success', 'Unidades sincronizadas com a Formação!');
                }}
                disabled={syncing}
                title="Sincronizar unidades orgânicas com o módulo de Formação"
                className="whitespace-nowrap flex items-center gap-2 px-3 py-2 border border-slate-200 text-slate-600 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors cursor-pointer disabled:opacity-50"
              >
                <i className={`${syncing ? 'ri-loader-4-line animate-spin' : 'ri-refresh-line'} text-sm`} />
                {syncing ? 'A sincronizar...' : 'Sincronizar Formação'}
              </button>
            )}
            <button
              onClick={() => openModal({ mode: 'nova-raiz' })}
              className="whitespace-nowrap flex items-center gap-2 px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors cursor-pointer"
            >
              <i className="ri-add-line" />
              Nova Unidade
            </button>
          </div>
        </div>

        {/* Barra de pesquisa */}
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3">
          <div className="relative flex-1">
            <div className="w-8 h-8 flex items-center justify-center absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
              <i className="ri-search-line text-sm" />
            </div>
            <input
              type="text"
              placeholder="Pesquisar unidades, subunidades..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-slate-400 hover:text-slate-700 cursor-pointer"
              >
                <i className="ri-close-line text-sm" />
              </button>
            )}
          </div>
          {/* Legenda compacta */}
          <div className="hidden sm:flex items-center gap-3 flex-shrink-0">
            {NIVEL_LABELS.map((label, i) => (
              <div key={label} className="flex items-center gap-1">
                <div className={`w-4 h-4 flex items-center justify-center rounded ${NIVEL_COLORS[i]}`}>
                  <i className={`${NIVEL_ICONS[i]} text-xs`} />
                </div>
                <span className="text-xs text-slate-400">{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Aviso de reordenação desativada durante pesquisa */}
        {searchQuery && (
          <div className="px-6 py-2 bg-amber-50/60 border-b border-amber-100 flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
              <i className="ri-information-line text-amber-500 text-xs" />
            </div>
            <p className="text-xs text-amber-600">
              Reordenação desativada durante a pesquisa.{' '}
              <button onClick={() => setSearch('')} className="whitespace-nowrap underline cursor-pointer hover:text-amber-800">Limpar pesquisa</button>
              {' '}para reordenar.
            </p>
            {visibleTree.length > 0 && (
              <span className="ml-auto text-xs text-amber-500 flex-shrink-0">
                {visibleTree.length} resultado{visibleTree.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        )}

        {/* Feedback */}
        {msg && (
          <div className={`mx-6 mt-3 flex items-center gap-2 p-3 rounded-lg border ${msg.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
              <i className={`text-sm ${msg.type === 'success' ? 'ri-checkbox-circle-line text-green-500' : 'ri-error-warning-line text-red-500'}`} />
            </div>
            <p className={`text-sm ${msg.type === 'success' ? 'text-green-700' : 'text-red-700'}`}>{msg.text}</p>
          </div>
        )}

        {/* Dica de reordenação */}
        {!searchQuery && tree.length > 1 && (
          <div className="px-6 py-2 border-b border-slate-50 flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
              <i className="ri-drag-move-line text-slate-300 text-xs" />
            </div>
            <p className="text-xs text-slate-300">
              Arrasta uma unidade para reordenar. Passa o rato para ver as setas de reordenação.
            </p>
          </div>
        )}

        {/* Árvore */}
        <div className="divide-y divide-slate-50 pb-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <i className="ri-loader-4-line text-2xl text-slate-300 animate-spin" />
            </div>
          ) : tree.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3 text-slate-400">
              <div className="w-12 h-12 flex items-center justify-center bg-slate-50 rounded-full">
                <i className="ri-building-2-line text-2xl" />
              </div>
              <p className="text-sm">Nenhuma unidade orgânica criada</p>
              <button onClick={() => openModal({ mode: 'nova-raiz' })} className="whitespace-nowrap text-xs text-slate-500 hover:text-slate-800 underline cursor-pointer">
                Criar primeira unidade
              </button>
            </div>
          ) : visibleTree.length === 0 && searchQuery ? (
            <div className="flex flex-col items-center justify-center py-10 gap-2 text-slate-400">
              <div className="w-10 h-10 flex items-center justify-center bg-slate-50 rounded-full">
                <i className="ri-search-line text-xl" />
              </div>
              <p className="text-sm">Nenhuma unidade encontrada para &quot;{search}&quot;</p>
              <button onClick={() => setSearch('')} className="whitespace-nowrap text-xs text-slate-500 hover:text-slate-800 underline cursor-pointer">Limpar pesquisa</button>
            </div>
          ) : (
            visibleTree.map((node, idx) => renderNode(node, 0, visibleTree, idx))
          )}
        </div>
      </div>

      {/* Modal criar/editar */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={closeModal}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div className="relative bg-white rounded-2xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-slate-800 text-base">
                  {modal.mode === 'nova-raiz' && 'Nova Unidade Orgânica'}
                  {modal.mode === 'novo-filho' && `Nova ${NIVEL_LABELS[Math.min((modal.paiNivel ?? 0) + 1, 2)]}`}
                  {modal.mode === 'editar' && `Editar ${NIVEL_LABELS[Math.min(getNivel(modal.item?.unidadePaiId ?? null), 2)]}`}
                </h3>
                {modal.mode === 'novo-filho' && modal.paiNome && (
                  <p className="text-xs text-slate-400 mt-0.5">Em: {modal.paiNome}</p>
                )}
                {modal.mode === 'editar' && modal.item?.unidadePaiId && (
                  <p className="text-xs text-slate-400 mt-0.5">
                    Dentro de: {allRows.find((r) => r.id === modal.item?.unidadePaiId)?.nome ?? '—'}
                  </p>
                )}
              </div>
              <button onClick={closeModal} className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="px-6 py-5">
              <label className="block text-sm font-medium text-slate-700 mb-2">Nome</label>
              <input
                type="text"
                value={inputNome}
                onChange={(e) => setInputNome(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
                placeholder="Ex: Divisão de Sistemas de Informação"
                autoFocus
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300"
              />
              <p className="text-xs text-slate-400 mt-2">Prima Enter ou clique em Guardar para confirmar.</p>
            </div>
            <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-3">
              <button onClick={closeModal} className="whitespace-nowrap px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer">Cancelar</button>
              <button onClick={handleSave} disabled={saving || !inputNome.trim()} className="whitespace-nowrap flex items-center gap-2 px-5 py-2 text-sm bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors disabled:opacity-50 cursor-pointer">
                {saving ? <><i className="ri-loader-4-line animate-spin" />A guardar...</> : <><i className="ri-save-line" />Guardar</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal confirmar eliminação */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setConfirmDelete(null)}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div className="relative bg-white rounded-2xl w-full max-w-sm overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-5">
              <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-full mx-auto mb-4">
                <i className="ri-delete-bin-line text-red-500 text-xl" />
              </div>
              <h3 className="font-bold text-slate-800 text-base text-center mb-1">Confirmar eliminação</h3>
              <p className="text-sm text-slate-500 text-center">
                Tens a certeza que queres eliminar <strong className="text-slate-700">&quot;{confirmDelete.nome}&quot;</strong>?
                {countAll(tree.find((n) => n.id === confirmDelete.id) ?? { filhos: [] } as UnidadeTree) > 0 && (
                  <span className="block mt-1 text-xs text-red-500">Todos os níveis abaixo serão também eliminados.</span>
                )}
              </p>
            </div>
            <div className="px-6 pb-5 flex items-center justify-center gap-3">
              <button onClick={() => setConfirmDelete(null)} className="whitespace-nowrap px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer">Cancelar</button>
              <button onClick={() => handleDelete(confirmDelete)} disabled={deletingId === confirmDelete.id} className="whitespace-nowrap flex items-center gap-2 px-5 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 cursor-pointer">
                {deletingId === confirmDelete.id ? <><i className="ri-loader-4-line animate-spin" />A eliminar...</> : <><i className="ri-delete-bin-line" />Eliminar</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}